import {
  type FieldSchema,
  makeFieldSchema,
} from '@backstage/plugin-scaffolder-react';

/**
 * The shared field schema of every picker, flat or hierarchical. The value
 * written to formData is the FULL selected-option value object under the
 * `{ id, name }` regime (matching the NS Cloud convention where a template
 * consumes `${{ parameters.field.name }}`), and the template-author-facing
 * `ui:options` formalises `dependsOn` (the cascade source refs).
 *
 * The output is `{ id, name }` open (`.passthrough()`) so an enrichable adapter
 * that returns extra fields is not rejected. Built with `makeFieldSchema` so
 * `createFormField` receives a real {@link FieldSchema} and a template's
 * `ui:options.dependsOn` is validated.
 *
 * Flat (`createChoiceFormField`) and hierarchical (`createHierarchicalFormField`)
 * both import THIS single definition, so their output and uiOptions can never
 * drift. Extracting it here is the owned contract that keeps the two field
 * flavours in lock-step with the contracts output schema.
 */
/**
 * Annotated explicitly rather than inferred: `makeFieldSchema` derives its
 * return type from the zod instance it uses internally, which resolves to a
 * nested copy under `@backstage/plugin-catalog-node/node_modules/zod`. TypeScript
 * cannot name that path portably and fails with TS2742 on a clean build (it
 * passes incrementally, so it only surfaced in CI). Writing the two output
 * shapes here keeps the type independent of where zod is hoisted.
 */
export const pickerFieldSchema: FieldSchema<
  { id: string; name: string } & Record<string, unknown>,
  { dependsOn?: string[] }
> = makeFieldSchema({
  output: z =>
    z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .passthrough(),
  uiOptions: z =>
    z.object({
      dependsOn: z.array(z.string()).optional(),
    }),
});
